import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { Building2, Eye, EyeOff } from 'lucide-react';

const Login = () => {
  const [isLogin, setIsLogin] = useState(true);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [nombre, setNombre] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const { login, register } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      if (isLogin) {
        const user = await login(email, password);
        toast.success(`Bienvenido, ${user.nombre}`);
        navigate(user.rol === 'admin' ? '/dashboard' : '/mi-cuenta');
      } else {
        const user = await register(email, password, nombre, 'admin');
        toast.success('Cuenta creada exitosamente');
        navigate('/dashboard');
      }
    } catch (error) {
      const message = error.response?.data?.detail || 'Error de autenticación';
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="login-page">
      {/* Hero Section */}
      <div className="login-hero">
        <img
          src="https://images.unsplash.com/photo-1754945496765-344dffca18d8?crop=entropy&cs=srgb&fm=jpg&q=85"
          alt="Industrial Park"
        />
        <div className="login-hero-content">
          <div className="flex items-center gap-3 mb-8">
            <img src="/naave-logo.png" alt="NAAVE" className="h-12" />
          </div>
          <h2 className="text-4xl font-black tracking-tight mb-4">
            Gestión Financiera de Parques Industriales
          </h2>
          <p className="text-slate-300 text-lg">
            Control de presupuestos, cuotas de mantenimiento e ingresos. 
            Todo en un solo lugar.
          </p>
        </div>
      </div>

      {/* Form Section */}
      <div className="login-form-container">
        <div className="login-form animate-fadeIn">
          <div className="mb-8">
            <h2 className="text-2xl font-black tracking-tight text-slate-900 mb-2" data-testid="login-title">
              {isLogin ? 'Iniciar Sesión' : 'Crear Cuenta'}
            </h2>
            <p className="text-slate-500">
              {isLogin 
                ? 'Ingresa tus credenciales para acceder al sistema' 
                : 'Crea una cuenta de administrador para comenzar'}
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-5">
            {!isLogin && (
              <div className="space-y-2">
                <Label htmlFor="nombre" className="label-overline">Nombre</Label>
                <Input
                  id="nombre"
                  type="text"
                  value={nombre}
                  onChange={(e) => setNombre(e.target.value)}
                  placeholder="Tu nombre completo"
                  required={!isLogin}
                  className="input-industrial h-12"
                  data-testid="input-nombre"
                />
              </div>
            )}

            <div className="space-y-2">
              <Label htmlFor="email" className="label-overline">Email</Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="correo@ejemplo.com"
                required
                className="input-industrial h-12"
                data-testid="input-email"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="password" className="label-overline">Contraseña</Label>
              <div className="relative">
                <Input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  required
                  className="input-industrial h-12 pr-12"
                  data-testid="input-password"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                >
                  {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                </button>
              </div>
            </div>

            <Button
              type="submit"
              className="w-full h-12 btn-industrial bg-slate-900 text-white hover:bg-slate-800"
              disabled={loading}
              data-testid="submit-btn"
            >
              {loading ? 'Procesando...' : isLogin ? 'Ingresar' : 'Crear Cuenta'}
            </Button>
          </form>

          <div className="mt-6 text-center">
            <button
              type="button"
              onClick={() => setIsLogin(!isLogin)}
              className="text-sm text-slate-500 hover:text-slate-900 transition-colors"
              data-testid="toggle-auth-mode"
            >
              {isLogin 
                ? '¿No tienes cuenta? Crear una' 
                : '¿Ya tienes cuenta? Iniciar sesión'}
            </button>
          </div>

          {/* Provider Registration Link */}
          <div className="mt-4 pt-4 border-t border-slate-200 text-center">
            <p className="text-sm text-slate-500 mb-2">¿Eres proveedor?</p>
            <a
              href="/registro-proveedor"
              className="text-sm text-amber-600 hover:text-amber-700 font-medium"
              data-testid="link-proveedor-registro"
            >
              Registrarse como Proveedor
            </a>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;
